import { flowRight } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from '../../../common/components/runtime-context';
import MoreButton from '../more-button';
import CategoryActions from '../../containers/category-actions';
import Link from '../link/internal-link';
import FollowButton from '../follow-button/follow-button';
import {
  isCacheableRendering,
  getIsForumWidthLessThan,
} from '../../../common/store/basic-params/basic-params-selectors';
import { REDUCERS } from '../../../common/components/runtime-context/reducers';
import withPermissions from '../../hoc/with-permissions';
import withFontClassName from '../../hoc/with-font-class-name';
import withTranslate from '../../../common/components/with-translate/with-translate';
import { isPrivate, SORT_BY_NEWEST } from '@wix/communities-forum-client-commons';
import { LockEmptyIcon } from '../icons/lock-empty-icon';
import { CurrencySignIcon } from '../icons/currency-sign-icon';
import { hasCategoryPaidPlans } from '../../selectors/paid-plans-selectors';
import FollowNotification from '../follow-notification';
import NewContentIndicator from '../new-content-indicator';
import { CommentIconSmall } from '../icons/comment-icon-small';
import { ViewCountIcon } from '../icons/view-count-icon';
import { PostBreakpoints } from '../responsive-listener';

import CounterNumber from '../counter-number';
import styles from './subcategory-list-classic-item-desktop.scss';

export class SubcategoryListItemDesktop extends Component {
  renderContentBlock = () => {
    const { titleFontClassName, category, hasCategoryPP } = this.props;
    let categoryBadge = null;

    if (isPrivate(category)) {
      const Icon = hasCategoryPP ? CurrencySignIcon : LockEmptyIcon;
      categoryBadge = (
        <span className={styles.categoryBadgeWrapper}>
          <Icon className="forum-icon-fill" />
        </span>
      );
    }
    const titleText = (
      <h2
        className={classNames(styles.title, titleFontClassName)}
        data-hook="subcategory-list-item__title"
      >
        {category.label} {categoryBadge}
      </h2>
    );
    return (
      <div className={styles.contentBlock}>
        {this.linkToPosts(titleText, 'subcategory-list-item__link')}
        <span className={styles.description} data-hook="subcategory-list-item__description">
          {category.description}
        </span>
      </div>
    );
  };

  handlePostFetch = (sort) => () => {
    const { category, fetchCategoryPosts, resetPostTypeFilter, setPostsPageSorting } = this.props;
    sort && setPostsPageSorting(sort, { dontReportBI: true });
    resetPostTypeFilter();
    fetchCategoryPosts({ categoryId: category._id, page: 1, sort });
  };

  linkToPosts = (children, dataHook, sort) => {
    const { category } = this.props;
    return (
      <Link
        className={classNames('forum-text-color', 'forum-link-hover-color')}
        to={`/${category.slug}`}
        onMouseDown={this.handlePostFetch(sort)}
        data-hook={dataHook}
      >
        {children}
      </Link>
    );
  };

  render() {
    const {
      category,
      contentFontClassName,
      isViewsCountEnabled,
      showViewCount,
      t,
      can,
      shouldWrap,
    } = this.props;

    const shouldShowMoreButton = can('edit', 'category', category);

    const moreButton = (
      <MoreButton size={MoreButton.SIZE_SMALL} data-hook="subcategory-list-item__category-actions">
        <CategoryActions hideFollowButton category={category} />
      </MoreButton>
    );
    const followButton = (
      <FollowButton
        category={category}
        className={shouldShowMoreButton ? styles.followButton : ''}
        data-hook="subcategory-list-item__follow-button"
        size={FollowButton.SIZE_SMALL}
      >
        <FollowNotification isFollowing={category.isSubscribed} />
      </FollowButton>
    );
    const categoryActions = (
      <div className={classNames({ [styles.fullRow]: shouldWrap })}>
        <div className={styles.actionsContainer}>
          {followButton}
          {shouldShowMoreButton && moreButton}
        </div>
      </div>
    );
    const categoryContent = (
      <div className={styles.main}>
        <div className={classNames(styles.contentPreview, 'forum-text-color')}>
          {this.renderContentBlock()}
        </div>

        <div className={styles.mainStats}>
          <div className={classNames(styles.numbers)}>
            {isViewsCountEnabled && (
              <div
                className={styles.numberElementWithIcon}
                data-hook="subcategory-list-item__total-views"
              >
                <ViewCountIcon
                  title={t('subcategory-list-item.views')}
                  className="forum-icon-fill"
                />
                <span className={classNames(styles.numberElementText)}>
                  {showViewCount ? (
                    <CounterNumber entityId={category._id} viewCount />
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </span>
              </div>
            )}

            <div
              className={styles.numberElementWithIcon}
              data-hook="subcategory-list-item__total-posts"
            >
              <CommentIconSmall
                title={t('subcategory-list-item.posts', 'subcategories-list-item__total-posts')}
                className="forum-icon-fill"
              />
              <span className={classNames(styles.numberElementText)}>
                <CounterNumber
                  initialValue={category.totalPosts}
                  entityId={category._id}
                  totalPosts
                />
              </span>
            </div>
          </div>
          {!!category.unseenPostsCount && (
            <div className={styles.recentActivity}>
              {this.linkToPosts(
                <NewContentIndicator count={category.unseenPostsCount} />,
                null,
                SORT_BY_NEWEST,
              )}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div
        className={classNames(styles.container, contentFontClassName, {
          [styles.containerWrap]: shouldWrap,
        })}
        data-hook="subcategory-list-item"
      >
        {categoryContent}
        {categoryActions}
      </div>
    );
  }
}

SubcategoryListItemDesktop.propTypes = {
  category: PropTypes.object.isRequired,
  titleFontClassName: PropTypes.string,
  contentFontClassName: PropTypes.string,
  isViewsCountEnabled: PropTypes.bool,
  showViewCount: PropTypes.bool,
  hasCategoryPP: PropTypes.bool,
  fetchCategoryPosts: PropTypes.func,
  resetPostTypeFilter: PropTypes.func,
  setPostsPageSorting: PropTypes.func,
  can: PropTypes.func,
  t: PropTypes.func,
};

const mapRuntimeToProps = (state, { category }, actions) => ({
  fetchCategoryPosts: actions.fetchCategoryPosts,
  resetPostTypeFilter: actions.resetPostTypeFilter,
  setPostsPageSorting: actions.setPostsPageSorting,
  showViewCount: !isCacheableRendering(state),
  hasCategoryPP: hasCategoryPaidPlans(state, category.groups),
  shouldWrap: getIsForumWidthLessThan(state, PostBreakpoints.xs),
});

export default flowRight(
  connect(mapRuntimeToProps, [REDUCERS.CATEGORIES, REDUCERS.BASIC_PARAMS, REDUCERS.PAID_PLANS]),
  withTranslate,
  withFontClassName,
  withPermissions,
)(SubcategoryListItemDesktop);
